import type { AdminAiTaskUsage, AdminAiUsage } from '@steado/admin-contract/ai-ops';

import { DataTable, type TableColumn, type TableRow } from '@/components/table/DataTable';
import { TextCell } from '@/components/table/cells';
import { Badge } from '@/components/ui/badge';
import { formatDuration, humanise } from '@/lib/format';
import { cacheHitRate, formatRate, formatUsd } from '@/lib/format-spend';

const COLUMNS: readonly TableColumn[] = [
  { key: 'task', label: 'Task', width: '1.35fr' },
  { key: 'calls', label: 'Calls', width: '0.6fr', align: 'right' },
  { key: 'cost', label: 'Cost', width: '0.75fr', align: 'right' },
  { key: 'perCall', label: 'Per call', width: '0.75fr', align: 'right' },
  { key: 'cache', label: 'Cached in', width: '0.7fr', align: 'right' },
  { key: 'latency', label: 'Avg ms', width: '0.7fr', align: 'right' },
  { key: 'model', label: 'Model that answered', width: '1.5fr' },
];

/**
 * Which model actually took the task, over what the router is configured to
 * send it to.
 *
 * A call served by something outside `expectedModels` is the one worth a
 * warning: it means a fallback chain reached past everything the running
 * process thinks it may use. Plan generation legitimately runs on three
 * models, which is why this compares against a list rather than against the
 * configured model alone.
 */
function ModelCell({ task }: { readonly task: AdminAiTaskUsage }) {
  const served = task.models[0];

  if (served === undefined) {
    return (
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate text-[13px] text-muted-foreground/60">no calls</span>
        <span className="truncate text-xs text-muted-foreground">{task.configuredModel}</span>
      </div>
    );
  }

  const offChain = !task.expectedModels.includes(served.model);
  const alsoRan = task.models.slice(1);

  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="truncate text-[13px] text-foreground">{served.model}</span>
        {offChain ? (
          <Badge tone="warn" size="sm" className="shrink-0" title="Not a model the router may send this to">
            off chain
          </Badge>
        ) : null}
        {alsoRan.length === 0 ? null : (
          <Badge
            tone="mute"
            size="sm"
            className="shrink-0"
            title={`also ran: ${alsoRan.map((model) => model.model).join(', ')}`}
          >
            also ran {alsoRan.length}
          </Badge>
        )}
      </span>
      <span className="truncate text-xs text-muted-foreground">
        {served.model === task.configuredModel ? 'as configured' : `configured: ${task.configuredModel}`}
      </span>
    </div>
  );
}

export function TaskTable({ usage }: { readonly usage: AdminAiUsage }) {
  const rows: readonly TableRow[] = usage.tasks.map((task) => ({
    id: task.task,
    cells: [
      <TextCell
        key="task"
        value={humanise(task.task)}
        sub={task.failures === 0 ? null : `${String(task.failures)} failed`}
        weight="medium"
        {...(task.failures === 0 ? {} : { tone: 'err' as const })}
      />,
      <TextCell key="calls" value={task.calls.toLocaleString('en-IN')} mono align="right" />,
      <TextCell
        key="cost"
        value={formatUsd(task.costUsd, task.costIsPartial)}
        mono
        align="right"
        weight="medium"
      />,
      <TextCell
        key="perCall"
        value={task.costUsd === null || task.calls === 0 ? '—' : formatUsd(task.costUsd / task.calls, false)}
        mono
        align="right"
      />,
      <TextCell
        key="cache"
        value={formatRate(cacheHitRate(task.cachedInputTokens, task.inputTokens))}
        mono
        align="right"
      />,
      <TextCell key="latency" value={formatDuration(task.avgLatencyMs)} mono align="right" />,
      <ModelCell key="model" task={task} />,
    ],
  }));

  return (
    <DataTable
      caption="Task by task"
      title="Task by task"
      count={`${String(usage.windowDays)} days`}
      columns={COLUMNS}
      rows={rows}
      isLoading={false}
      emptyTitle="No model calls in this window"
      emptyBody="Nothing reached a model in the last window."
    />
  );
}
